import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { DotsThreeVertical, DotsSixVertical, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import SubJobRow from "./SubJobRow";

const move = (arr, from, to) => { const a = [...arr]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; };

/* Drag (or ↑/↓) the sub-jobs into the order the trades should attend; saved as the sub-job number */
export default function SubJobsSortableList({ parentId, rows, onReordered, onDetach }) {
  const [items, setItems] = useState(rows);
  useEffect(() => { setItems(rows); }, [rows]);

  const save = async (next) => {
    const renumbered = next.map((c, i) => ({ ...c, sub_job_index: i + 1 }));
    setItems(renumbered);
    try {
      await API.put(`/jobs/${parentId}/sub-jobs/order`, { order: renumbered.map((c) => c.id) });
      toast.success("Sequence saved", { duration: 1500 });
      onReordered?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save the order"); setItems(rows); }
  };
  const onDragEnd = (r) => { if (r.destination && r.destination.index !== r.source.index) save(move(items, r.source.index, r.destination.index)); };
  const nudge = (i, d) => { const j = i + d; if (j >= 0 && j < items.length) save(move(items, i, j)); };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={`sub-jobs-${parentId}`}>
        {(drop) => (
          <div ref={drop.innerRef} {...drop.droppableProps} className="border-t border-border divide-y divide-border" data-testid="sub-jobs-list">
            {items.map((c, i) => (
              <Draggable key={c.id} draggableId={c.id} index={i}>
                {(drag, snap) => (
                  <div ref={drag.innerRef} {...drag.draggableProps} className={`flex items-stretch bg-card ${snap.isDragging ? "shadow-lg ring-1 ring-violet-300 rounded-md" : ""}`} data-testid={`sub-job-sortable-${c.id}`}>
                    <div {...drag.dragHandleProps} className="flex items-center pl-2 text-muted-foreground/60 hover:text-violet-600 cursor-grab active:cursor-grabbing" title="Drag to change the attendance order" data-testid={`sub-job-drag-${c.id}`}>
                      <DotsSixVertical size={14} weight="bold" />
                    </div>
                    <SubJobRow job={c} to={`/jobs/${c.id}`} testid={`sub-job-row-${c.id}`} className="flex-1"
                      actions={(
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-7 w-7 p-0 mr-1" data-testid={`sub-job-menu-${c.id}`}><DotsThreeVertical size={14} /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild><Link to={`/jobs/${c.id}`}>Open sub-job</Link></DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled={i === 0} onClick={() => nudge(i, -1)} data-testid={`sub-job-move-up-${c.id}`}><ArrowUp size={12} className="mr-2" /> Move up</DropdownMenuItem>
                            <DropdownMenuItem disabled={i === items.length - 1} onClick={() => nudge(i, 1)} data-testid={`sub-job-move-down-${c.id}`}><ArrowDown size={12} className="mr-2" /> Move down</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => onDetach(c)} data-testid={`sub-job-detach-${c.id}`}>Detach from main job</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )} />
                  </div>
                )}
              </Draggable>
            ))}
            {drop.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
